import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { dealAPI, relationshipAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import SubscriptionLock from '@/components/SubscriptionLock';
import { hasFullAccess } from '@/lib/subscription';
import { extractErrorMessage } from '@/lib/errors';
import { Plus, Tag, Percent, Calendar, Loader2, Ban, Users, Share2, Pencil } from 'lucide-react';

// Unit list for the Min. Purchase Amount picker. Kept identical to the one
// on ProductsPage so a manufacturer sees the same vocabulary everywhere —
// and so we can reuse the existing `products.units.*` i18n keys for the
// labels without spinning up a duplicate namespace.
const MIN_PURCHASE_UNIT_KEYS = ['piece', 'kg', 'g', 'litre', 'ml', 'bag', 'pack', 'bottle', 'carton'];

// Whose deals a given role acts on:
//   - `receivedFrom` → the role whose deals show up on the "received" tab
//     (only rendered when non-null; a manufacturer has no upstream deals to
//     see here so we skip the tab for them).
//   - `sellsTo`      → the role a user can target when creating their own
//     deals; drives filtering of the target-buyer picker so a distributor
//     only sees shopkeepers as targets, never manufacturers.
//   - `receiveOnly`  → farmers can only RECEIVE deals; they can't publish
//     their own. The page renders a single list with no tabs and no
//     create UI. The home-screen carousel deep-links here with
//     `?deal=<id>` so a tap jumps the user to the exact card.
const ROLE_DEAL_MAP = {
  manufacturer: { receivedFrom: null,           sellsTo: 'distributor' },
  distributor:  { receivedFrom: 'manufacturer', sellsTo: 'shopkeeper'  },
  shopkeeper:   { receivedFrom: 'distributor',  sellsTo: 'farmer'      },
  farmer:       { receivedFrom: null,           sellsTo: null, receiveOnly: true },
};

// Who a deal can be for (PR 72), per seller: the choices shown on the
// create form, the first being the default. Shopkeepers' deals always go
// to farmers in their area (no choice). Matches deal_reach.ALLOWED.
const AUDIENCE_CHOICES = {
  manufacturer: [['distributor'], ['shopkeeper'], ['distributor', 'shopkeeper']],
  distributor: [['shopkeeper'], ['farmer'], ['shopkeeper', 'farmer']],
  shopkeeper: [['farmer']],
};
const sameRoles = (a, b) => a.length === b.length && a.every((r) => b.includes(r));

export default function DealsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const userType = user?.user_type || 'manufacturer';
  const roleMap = ROLE_DEAL_MAP[userType] || ROLE_DEAL_MAP.manufacturer;
  const showReceivedTab = Boolean(roleMap.receivedFrom);
  const isReceiveOnly = Boolean(roleMap.receiveOnly);

  // Two separate lists: deals THIS user created (Mine) and deals from the
  // upstream role that this user can benefit from (Received). Receievd only
  // makes sense for distributors/shopkeepers today; manufacturers see the
  // Mine list alone in a plain layout.
  const [myDeals, setMyDeals] = useState([]);
  const [receivedDeals, setReceivedDeals] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // ?tab=mine or ?tab=received deep-links to a specific tab; falls back to
  // the received tab for roles that have one, else mine.
  const initialTab = (() => {
    const requested = searchParams.get('tab');
    if (requested === 'mine' || requested === 'received') {
      if (requested === 'received' && !showReceivedTab) return 'mine';
      return requested;
    }
    return showReceivedTab ? 'received' : 'mine';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  // ?create=1 (only on Mine tab) opens the Create Deal dialog on mount.
  // Consumed once, then cleared from the URL so a refresh doesn't reopen it.
  const [showModal, setShowModal] = useState(() => {
    return searchParams.get('create') === '1' && initialTab === 'mine';
  });
  useEffect(() => {
    if (searchParams.get('create') === '1' || searchParams.get('tab') || searchParams.get('start')) {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      next.delete('tab');
      next.delete('start');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [saving, setSaving] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const audienceChoices = AUDIENCE_CHOICES[userType] || [];
  const [audience, setAudience] = useState(audienceChoices[0] || []);
  // Set when a shopkeeper edits a deal from their distributor into their own.
  const [sourceDeal, setSourceDeal] = useState(null);
  const [passingOn, setPassingOn] = useState(null);
  // The deal currently being ended (drives the End-Deal dialog). Once a deal
  // is published we intentionally allow no other mutation — only "put an end
  // date on it", which stops it applying while preserving the record.
  const [endingDeal, setEndingDeal] = useState(null);
  const [endDateInput, setEndDateInput] = useState('');
  const [ending, setEnding] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deal_type: 'percentage',
    discount_value: '',
    min_purchase_amount: '',
    min_purchase_unit: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });

  useEffect(() => {
    loadDeals();
    // Farmers can't publish deals, so there's no need to load their
    // connected-users list (which drives the target-buyer picker on
    // the create form). Skipping the call avoids a wasted network
    // round-trip on every farmer visit.
    if (!isReceiveOnly) loadConnectedUsers();
  }, [user.id]);

  // Farmers can't publish their own deals — they only receive. We
  // load the same buyer-targeted list the home-screen carousel uses
  // and render it as one plain, read-only grid (no tabs, no create).
  // A `?deal=<id>` URL param (set by the home carousel) scrolls to
  // and briefly highlights the matching card.
  const [farmerDeals, setFarmerDeals] = useState([]);
  const highlightedDealId = searchParams.get('deal');
  const highlightCardRefs = useRef({});

  const loadDeals = async () => {
    try {
      if (isReceiveOnly) {
        const res = await dealAPI.getBuyerDeals(user.id);
        setFarmerDeals(res.data || []);
        return;
      }
      const requests = [dealAPI.getSellerDeals(user.id)];
      if (showReceivedTab) requests.push(dealAPI.getBuyerDeals(user.id));
      const [mineRes, receivedRes] = await Promise.all(requests);

      setMyDeals(mineRes.data || []);

      if (showReceivedTab) {
        const all = receivedRes?.data || [];
        // getBuyerDeals returns any active public deal + any targeted at me.
        // For the "Manufacturer's Deals" tab we only want ones from the
        // configured upstream role, and never our own public deals.
        const wantFrom = roleMap.receivedFrom;
        setReceivedDeals(
          all.filter((d) => d.seller_type === wantFrom && d.seller_id !== user.id),
        );
      }
    } catch (error) {
      console.error('Failed to load deals', error);
    } finally {
      setLoading(false);
    }
  };

  // Scroll + highlight for `?deal=<id>` deep links (home carousel
  // writes the id when the user taps a tile). Runs once the deals
  // list has materialised; the ref population happens as cards
  // render, so a short rAF defers the scroll until layout is settled.
  useEffect(() => {
    if (!highlightedDealId || loading) return;
    const node = highlightCardRefs.current[highlightedDealId];
    if (!node) return;
    const id = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [highlightedDealId, farmerDeals.length, myDeals.length, receivedDeals.length, loading]);

  const loadConnectedUsers = async () => {
    try {
      const res = await relationshipAPI.getConnectedUsers(user.id);
      setConnectedUsers(res.data || []);
    } catch (error) {
      console.error('Failed to load connected users', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.discount_value || !formData.end_date) {
      toast({ title: t('deals.toast.requiredFields'), variant: 'destructive' });
      return;
    }

    // Discount and min-purchase are money amounts — a negative value doesn't
    // exist in the domain and the input is a bare <type="number"> box which
    // otherwise accepts one. Guard explicitly so the backend never has to.
    const discountNum = parseFloat(formData.discount_value);
    if (!Number.isFinite(discountNum) || discountNum < 0) {
      toast({ title: t('deals.toast.discountNegative'), variant: 'destructive' });
      return;
    }
    if (formData.min_purchase_amount !== '' && parseFloat(formData.min_purchase_amount) < 0) {
      toast({ title: t('deals.toast.minPurchaseNegative'), variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const dealData = {
        title: formData.title,
        description: formData.description,
        deal_type: formData.deal_type,
        discount_value: parseFloat(formData.discount_value),
        min_purchase_amount: formData.min_purchase_amount ? parseFloat(formData.min_purchase_amount) : null,
        min_purchase_unit: formData.min_purchase_amount && formData.min_purchase_unit ? formData.min_purchase_unit : null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        // Public deals: empty list = "no targeting restriction". Null
        // also now works (PR 3 made target_buyers Optional[List[str]])
        // but keeping [] here is explicit — the deal-visibility logic
        // already treats [] as "public".
        target_buyers: selectedTargets.length > 0 ? selectedTargets : [],
        audience,
        ...(sourceDeal ? { source_deal_id: sourceDeal.id } : {}),
      };
      await dealAPI.create(dealData);
      toast({ title: t('deals.toast.created') });
      setShowModal(false);
      setFormData({
        title: '', description: '', deal_type: 'percentage', discount_value: '',
        min_purchase_amount: '', min_purchase_unit: '',
        start_date: new Date().toISOString().split('T')[0], end_date: '',
      });
      setSelectedTargets([]);
      setAudience(audienceChoices[0] || []);
      setSourceDeal(null);
      loadDeals();
    } catch (error) {
      toast({ title: extractErrorMessage(error, t('deals.toast.createFailed')), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // A shopkeeper passes a distributor's deal on to the farmers in their
  // area as it is, or opens it in the create form to change it first.
  const dealTerms = (deal) => ({
    title: deal.title || '',
    description: deal.description || '',
    deal_type: deal.deal_type || 'percentage',
    discount_value: deal.discount_value ?? '',
    min_purchase_amount: deal.min_purchase_amount ?? '',
    min_purchase_unit: deal.min_purchase_unit || '',
  });

  const passOn = async (deal) => {
    if (!window.confirm(t('deals.passOn.confirm', { title: deal.title }))) return;
    setPassingOn(deal.id);
    try {
      const terms = dealTerms(deal);
      await dealAPI.create({
        ...terms,
        discount_value: Number(terms.discount_value),
        min_purchase_amount: terms.min_purchase_amount === '' ? null : Number(terms.min_purchase_amount),
        min_purchase_unit: terms.min_purchase_unit || null,
        start_date: new Date().toISOString(),
        end_date: deal.end_date,
        target_buyers: [],
        audience: ['farmer'],
        source_deal_id: deal.id,
      });
      toast({ title: t('deals.passOn.done') });
      setActiveTab('mine');
      loadDeals();
    } catch (error) {
      toast({ title: extractErrorMessage(error, t('deals.toast.createFailed')), variant: 'destructive' });
    } finally {
      setPassingOn(null);
    }
  };

  const editAsMine = (deal) => {
    setFormData({
      ...dealTerms(deal),
      discount_value: String(deal.discount_value ?? ''),
      min_purchase_amount: deal.min_purchase_amount ? String(deal.min_purchase_amount) : '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: deal.end_date ? String(deal.end_date).split('T')[0] : '',
    });
    setAudience(['farmer']);
    setSourceDeal(deal);
    setActiveTab('mine');
    setShowModal(true);
  };

  const toggleTarget = (userId) => {
    setSelectedTargets(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const todayIso = () => new Date().toISOString().split('T')[0];

  const openEndDialog = (deal) => {
    setEndingDeal(deal);
    // Default the picker to today — the common case is "end this deal now".
    // The manufacturer can dial it forward if they want the deal to keep
    // running for a bit longer, but never backwards past today.
    setEndDateInput(todayIso());
  };

  const closeEndDialog = () => {
    setEndingDeal(null);
    setEndDateInput('');
  };

  const handleConfirmEnd = async () => {
    if (!endingDeal || !endDateInput) return;
    setEnding(true);
    try {
      const pickedIso = new Date(endDateInput).toISOString();
      const isImmediate = new Date(endDateInput) <= new Date();
      // status=terminated makes an ended deal easy to identify regardless of
      // the end-date value; only set it when the picked date has already
      // passed, so a "future end date" keeps the deal live until then.
      await dealAPI.update(endingDeal.id, {
        ...(isImmediate ? { status: 'terminated' } : {}),
        end_date: pickedIso,
      });
      toast({ title: t('deals.toast.ended') });
      closeEndDialog();
      loadDeals();
    } catch (error) {
      console.error('End deal failed', error);
      toast({ title: extractErrorMessage(error, t('deals.toast.endFailed')), variant: 'destructive' });
    } finally {
      setEnding(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-farm-primary" /></div>;
  }

  const hasSubscription = hasFullAccess(user);
  // Only offer people this user can actually sell to as deal targets — a
  // distributor selects shopkeepers, a manufacturer selects distributors,
  // etc. Falls back to the full connected set when the mapping is unknown.
  // Only people in the chosen audience can be named as targets.
  const targetableUsers = audience.length
    ? connectedUsers.filter((u) => audience.includes(u.user_type))
    : connectedUsers;

  if (!hasSubscription) {
    return (
      <SubscriptionLock
        bodyKey="subscriptionLock.body.deals"
        userType={userType}
        benefitKeys={['dealMgmt', 'promotions', 'networkAccess', 'analyticsShort']}
      />
    );
  }

  const discountLabel = formData.deal_type === 'percentage'
    ? t('deals.form.discountPercent')
    : t('deals.form.discountAmount');

  // Render either the "mine" grid (owner can End their deals) or the
  // "received" grid (read-only view of upstream deals available to you).
  // Empty state text is per-mode so the copy matches the tab.
  const renderDealList = (list, { readOnly, mode }) => {
    if (list.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Tag className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">
              {t(`deals.empty.${mode}.title`)}
            </h3>
            <p className="text-muted-foreground mb-4 text-center max-w-md">
              {t(`deals.empty.${mode}.subtitle`)}
            </p>
            {!readOnly && (
              <Button onClick={() => setShowModal(true)} className="bg-farm-secondary hover:bg-farm-secondary-light">
                <Plus className="w-4 h-4 mr-2" /> {t('deals.createFirst')}
              </Button>
            )}
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((deal) => {
          const isEnded = deal.status === 'terminated' || (deal.end_date && new Date(deal.end_date) < new Date());
          return (
          <Card
            key={deal.id}
            ref={(el) => {
              if (el) highlightCardRefs.current[deal.id] = el;
              else delete highlightCardRefs.current[deal.id];
            }}
            className={`hover:shadow-farm-lg transition-shadow border-farm-secondary/30 flex flex-col ${isEnded ? 'opacity-70' : ''} ${
              highlightedDealId === deal.id
                ? 'ring-2 ring-farm-gold ring-offset-2 shadow-farm-lg'
                : ''
            }`}
            data-testid={`deal-${deal.id}`}
          >
            <CardContent className="p-4 flex flex-col gap-3 h-full">
              {/* Row 1 — icon on the left, "Ended" pill / End button (owned
                  deals only) / seller badge (received deals) on the right. */}
              <div className="flex items-start justify-between gap-2">
                <div className="w-10 h-10 bg-farm-secondary/10 rounded-xl flex items-center justify-center shrink-0">
                  <Percent className="w-5 h-5 text-farm-secondary" />
                </div>
                <div className="flex items-center shrink-0">
                  {isEnded ? (
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted px-2 py-1 rounded-md" data-testid={`ended-badge-${deal.id}`}>
                      {t('deals.card.endedBadge')}
                    </span>
                  ) : readOnly ? (
                    <span className="text-xs font-semibold uppercase tracking-wide text-farm-primary bg-farm-primary/10 px-2 py-1 rounded-md">
                      {t('deals.card.availableBadge')}
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => openEndDialog(deal)} className="h-8 text-destructive hover:text-destructive gap-1.5" data-testid={`end-deal-${deal.id}`}>
                      <Ban className="w-4 h-4" />
                      <span className="text-xs">{t('deals.card.endBtn')}</span>
                    </Button>
                  )}
                </div>
              </div>

              {/* Row 2 — big discount badge on its own line. */}
              <div className="text-3xl font-bold text-farm-secondary leading-none whitespace-nowrap">
                {deal.deal_type === 'percentage'
                  ? t('deals.card.percentOff', { value: deal.discount_value })
                  : t('deals.card.amountOff', { value: deal.discount_value })}
              </div>

              {/* Row 3 — title + description. */}
              <div className="min-w-0">
                <h3 className="font-semibold text-base leading-snug break-words">{deal.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words">
                  {deal.description || t('deals.card.noDescription')}
                </p>
              </div>

              {/* Footer meta — each on its own row so nothing gets clipped */}
              <div className="mt-auto pt-3 border-t space-y-1.5 text-xs text-muted-foreground">
                {/* On received deals show who the deal is from — the seller.
                    On own deals the seller is you, so we skip this line. */}
                {readOnly && deal.seller_name && (
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">
                      {t('deals.card.from', { name: deal.seller_name })}
                    </span>
                  </div>
                )}
                {deal.end_date && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">
                      {t('deals.card.until', { date: new Date(deal.end_date).toLocaleDateString(i18n.language) })}
                    </span>
                  </div>
                )}
                {deal.min_purchase_amount ? (
                  <div className="flex items-center gap-1.5">
                    <span className="truncate">
                      {deal.min_purchase_unit
                        ? t('deals.card.minPurchaseWithUnit', {
                            amount: deal.min_purchase_amount,
                            unit: t(`products.units.${deal.min_purchase_unit}`, deal.min_purchase_unit),
                          })
                        : t('deals.card.minPurchase', { amount: deal.min_purchase_amount })}
                    </span>
                  </div>
                ) : null}
                {!readOnly && (deal.audience || []).length > 0 && (
                  <div className="flex items-center gap-1.5" data-testid={`deal-audience-${deal.id}`}>
                    <Users className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">
                      {t('deals.audience.for', { who: deal.audience.map((r) => t(`deals.audience.roles.${r}`)).join(', ') })}
                    </span>
                  </div>
                )}
                {!readOnly && deal.source_seller_name && (
                  <div className="flex items-center gap-1.5">
                    <Share2 className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{t('deals.passOn.from', { name: deal.source_seller_name })}</span>
                  </div>
                )}
                {!readOnly && deal.target_buyer_names && deal.target_buyer_names.length > 0 && (
                  <div className="flex items-start gap-1.5">
                    <Users className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span className="line-clamp-2 break-words">
                      {t('deals.card.targeted', { names: deal.target_buyer_names.join(', ') })}
                    </span>
                  </div>
                )}
              </div>
              {readOnly && mode === 'received' && userType === 'shopkeeper' && !isEnded && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Button size="sm" className="bg-farm-primary hover:bg-farm-primary-dark" disabled={passingOn === deal.id}
                          onClick={() => passOn(deal)} data-testid={`pass-on-${deal.id}`}>
                    {passingOn === deal.id ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <><Share2 className="w-4 h-4 mr-1" /> {t('deals.passOn.button')}</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => editAsMine(deal)} data-testid={`edit-own-${deal.id}`}>
                    <Pencil className="w-4 h-4 mr-1" /> {t('deals.passOn.edit')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
        })}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('deals.title')}</h1>
          <p className="text-muted-foreground">{t('deals.subtitle')}</p>
        </div>
        {/* Farmers can't publish deals, so the Create button is
            hidden entirely. Other roles see it (invisible while on
            the Received tab since they can't edit upstream deals). */}
        {!isReceiveOnly && (
          <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) setSourceDeal(null); }}>
            <DialogTrigger asChild>
              {/* Create belongs to Mine — hidden while browsing upstream deals
                  since a distributor can't edit a manufacturer's deal. */}
              <Button
                className={`bg-farm-secondary hover:bg-farm-secondary-light ${
                  showReceivedTab && activeTab === 'received' ? 'invisible' : ''
                }`}
                data-testid="create-deal-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('deals.create')}
              </Button>
            </DialogTrigger>
            <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('deals.dialog')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>{t('deals.form.titleLabel')}</Label>
                <Input placeholder={t('deals.form.titlePlaceholder')} value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} data-testid="deal-title-input" />
              </div>
              <div className="space-y-2">
                <Label>{t('deals.form.description')}</Label>
                <Textarea placeholder={t('deals.form.descriptionPlaceholder')} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('deals.form.type')}</Label>
                  <Select value={formData.deal_type} onValueChange={(value) => setFormData({...formData, deal_type: value})}>
                    <SelectTrigger><SelectValue placeholder={t('deals.form.typePlaceholder')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">{t('deals.form.typePercentage')}</SelectItem>
                      <SelectItem value="fixed_amount">{t('deals.form.typeFixed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{discountLabel} *</Label>
                  <Input type="number" min="0" step="0.01" placeholder={formData.deal_type === 'percentage' ? '10' : '500'} value={formData.discount_value} onChange={(e) => setFormData({...formData, discount_value: e.target.value})} data-testid="deal-discount-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('deals.form.startDate')}</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} data-testid="deal-start-date" />
                </div>
                <div className="space-y-2">
                  <Label>{t('deals.form.endDate')}</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} data-testid="deal-end-date" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('deals.form.minPurchase')}</Label>
                <div className="grid grid-cols-[1fr_140px] gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t('deals.form.minPurchasePlaceholder')}
                    value={formData.min_purchase_amount}
                    onChange={(e) => setFormData({...formData, min_purchase_amount: e.target.value})}
                    data-testid="deal-minpurchase-amount"
                  />
                  <Select value={formData.min_purchase_unit} onValueChange={(v) => setFormData({...formData, min_purchase_unit: v})}>
                    <SelectTrigger data-testid="deal-minpurchase-unit">
                      <SelectValue placeholder={t('deals.form.unitPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {MIN_PURCHASE_UNIT_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>{t(`products.units.${key}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">{t('deals.form.minPurchaseHint')}</p>
              </div>

              {/* Who the deal is for (PR 72). */}
              {audienceChoices.length > 1 ? (
                <div className="space-y-2">
                  <Label>{t('deals.audience.label')}</Label>
                  <div className="grid grid-cols-3 gap-2" data-testid="deal-audience">
                    {audienceChoices.map((choice) => {
                      const on = sameRoles(choice, audience);
                      return (
                        <button key={choice.join('+')} type="button"
                                onClick={() => { setAudience(choice); setSelectedTargets([]); }}
                                className={`rounded-lg border-2 px-2 py-2 text-sm font-semibold ${on ? 'border-farm-primary bg-farm-primary/10' : 'border-gray-200'}`}
                                aria-pressed={on} data-testid={`deal-audience-${choice.join('-')}`}>
                          {choice.length > 1 ? t('deals.audience.both')
                            : t(`deals.audience.only.${choice[0]}`)}
                        </button>
                      );
                    })}
                  </div>
                  {audience.includes('farmer') && (
                    <p className="text-xs text-muted-foreground">{t('deals.audience.farmerHint')}</p>
                  )}
                </div>
              ) : audience.includes('farmer') && (
                <p className="text-sm text-muted-foreground" data-testid="deal-audience-fixed">{t('deals.audience.shopHint')}</p>
              )}
              {sourceDeal && (
                <p className="text-xs text-muted-foreground">{t('deals.passOn.editing', { name: sourceDeal.seller_name || '' })}</p>
              )}

              {/* Target Buyers Selection — narrowed to the role this user
                  actually sells to (distributors → shopkeepers, manufacturers
                  → distributors, shopkeepers → farmers). Prevents a
                  distributor from targeting a manufacturer by accident. */}
              {targetableUsers.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> {t('deals.form.targets')}
                  </Label>
                  <p className="text-xs text-muted-foreground">{t('deals.form.targetsHint')}</p>
                  <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                    {targetableUsers.map((cu) => (
                      <div key={cu.id} className="flex items-center gap-2">
                        <Checkbox id={`target-${cu.id}`} checked={selectedTargets.includes(cu.id)} onCheckedChange={() => toggleTarget(cu.id)} />
                        <label htmlFor={`target-${cu.id}`} className="text-sm cursor-pointer flex-1">
                          {cu.full_name} <span className="text-muted-foreground">{t('deals.form.targetRoleSuffix', { role: t(`common.role.${cu.user_type}`, cu.user_type) })}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">{t('common.cancel')}</Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-farm-secondary hover:bg-farm-secondary-light" data-testid="save-deal-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('deals.form.submit')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {isReceiveOnly ? (
        renderDealList(farmerDeals, { readOnly: true, mode: 'received' })
      ) : showReceivedTab ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-4">
            <TabsTrigger value="received" data-testid="deals-tab-received">
              {t(`deals.tabs.received.${userType}`)}
            </TabsTrigger>
            <TabsTrigger value="mine" data-testid="deals-tab-mine">
              {t(`deals.tabs.mine.${userType}`)}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="received" className="mt-0">
            {/* A shop's deal starts here (PR 74): pass on or edit one of his
                distributor's deals, or make a new one. */}
            {userType === 'shopkeeper' && (
              <div className="rounded-2xl border-2 border-farm-primary/30 bg-farm-primary/5 p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3"
                   data-testid="deals-start-card">
                <p className="text-sm flex-1">{t('deals.start.hint')}</p>
                <Button onClick={() => { setActiveTab('mine'); setShowModal(true); }}
                        className="bg-farm-secondary hover:bg-farm-secondary-light shrink-0" data-testid="deals-start-new">
                  <Plus className="w-4 h-4 mr-2" /> {t('deals.start.new')}
                </Button>
              </div>
            )}
            {renderDealList(receivedDeals, { readOnly: true, mode: 'received' })}
          </TabsContent>
          <TabsContent value="mine" className="mt-0">
            {renderDealList(myDeals, { readOnly: false, mode: 'mine' })}
          </TabsContent>
        </Tabs>
      ) : (
        renderDealList(myDeals, { readOnly: false, mode: 'mine' })
      )}

      {/* End-Deal dialog — the only mutation available on an existing deal.
          The user picks the end date (today by default) and confirms; the
          deal stays on the list with an "Ended" label so history is kept. */}
      <Dialog open={!!endingDeal} onOpenChange={(open) => { if (!open) closeEndDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deals.endDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              {t('deals.endDialog.body', { title: endingDeal?.title || '' })}
            </p>
            <div className="space-y-2">
              <Label>{t('deals.endDialog.dateLabel')}</Label>
              <Input
                type="date"
                value={endDateInput}
                onChange={(e) => setEndDateInput(e.target.value)}
                min={todayIso()}
                data-testid="end-deal-date-input"
              />
              <p className="text-xs text-muted-foreground">{t('deals.endDialog.hint')}</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={closeEndDialog} className="flex-1">
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                disabled={ending || !endDateInput}
                onClick={handleConfirmEnd}
                className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                data-testid="end-deal-confirm-btn"
              >
                {ending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('deals.endDialog.confirm')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
